import { useState, useCallback, useMemo } from "react";

interface UseStepperProps {
  steps: number;
  initialStep?: number;
  onComplete?: () => void;
  validateStep?: (step: number) => boolean;
}

interface UseStepperReturn {
  currentStep: number;
  isFirstStep: boolean;
  isLastStep: boolean;
  canGoNext: boolean;
  goToStep: (step: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  reset: () => void;
  totalSteps: number;
  progress: number;
  stepIndex: number;
  onValidateStep: (step: number, errorMessage?: string) => void;
  removeErrorStep: (step: number) => void;
  errorsStep: Record<number, string>; // {step: errorMessage}
}

export default function useStepper({
  steps,
  initialStep = 0,
  onComplete,
  validateStep,
}: UseStepperProps): UseStepperReturn {
  const [currentStep, setCurrentStep] = useState<number>(initialStep);
  const [errorsStep, setErrorsStep] = useState<Record<number, string>>({});

  const removeErrorStep = (step: number) => {
    setErrorsStep((prev) => {
      // @ts-expect-error - This is a workaround to remove the error from the step
      const { [step]: _, ...rest } = prev;
      return rest;
    });
  };

  const onValidateStep = (step: number, errorMessage?: string) => {
    if (errorsStep?.[step] && !errorMessage) {
      removeErrorStep(step);
    }
    setErrorsStep((prev) => ({ ...prev, [step]: errorMessage ?? "" }));
  };

  const isFirstStep = useMemo(() => currentStep === 0, [currentStep]);
  const isLastStep = useMemo(
    () => currentStep === steps - 1,
    [currentStep, steps]
  );
  const totalSteps = steps;
  const stepIndex = currentStep;

  // Calculate progress percentage
  const progress = useMemo(() => {
    return Math.round(((currentStep + 1) / steps) * 100);
  }, [currentStep, steps]);

  // Default to true if no validation function is provided
  const canGoNext = useMemo(() => {
    if (!validateStep) return true;
    if (errorsStep?.[currentStep]) {
      // If there is an error, don't allow to go to next step
      return false;
    }
    return validateStep(currentStep);
  }, [currentStep, validateStep, errorsStep]);

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < steps) {
        setCurrentStep(step);
      }
    },
    [steps]
  );

  const goToNext = useCallback(() => {
    if (currentStep < steps - 1) {
      setCurrentStep((prev) => prev + 1);
    } else if (isLastStep && onComplete) {
      onComplete();
    }
  }, [currentStep, steps, isLastStep, onComplete]);

  const goToPrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const reset = useCallback(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);

  return {
    currentStep,
    isFirstStep,
    isLastStep,
    canGoNext,
    goToStep,
    goToNext,
    goToPrevious,
    reset,
    totalSteps,
    progress,
    stepIndex,
    onValidateStep,
    removeErrorStep,
    errorsStep,
  };
}
